import {
  amountShare,
  formatMoney,
  isAmountAbove,
  subtractAmounts,
  type Money,
} from '@platform/design-system';
import type { Operation, RequestBody, ResponseBody } from '@platform/openapi';

export type ProductList = ResponseBody<Operation<'/admin/products', 'get'>>;
export type Product = ProductList['items'][number];
export type ProductVariant = Product['variants'][number];
export type ProductStatus = Product['status'];
export type CreateProductBody = RequestBody<Operation<'/admin/products', 'post'>>;
export type TenantDetail = ResponseBody<Operation<'/admin/tenant', 'get'>>;

export const COST_ROLE = 'catalog:cost';
export const CATALOG_WRITE_ROLE = 'catalog:write';

export const PRODUCT_STATUSES = ['draft', 'active', 'archived'] as const satisfies readonly ProductStatus[];

export const productStatusTone = { draft: 'neutral', active: 'success', archived: 'warning' } as const;

export const firstVariant = (product: Product): ProductVariant | undefined => product.variants[0];

export type ProductImage = Product['media'][number];

export const coverOf = (product: Product): ProductImage | undefined => product.media[0];

export type PriceSummary = {
  readonly lowest: Money;
  readonly highest: Money;
  readonly compareAt: Money | null;
};

const byAmount = (left: Money, right: Money): number =>
  isAmountAbove(left.amount, right.amount) ? 1 : isAmountAbove(right.amount, left.amount) ? -1 : 0;

export const priceSummary = (product: Product): PriceSummary | null => {
  const prices = product.variants.map((variant) => variant.price).sort(byAmount);
  const lowest = prices[0];
  const highest = prices.at(-1);
  if (lowest === undefined || highest === undefined) return null;
  return { lowest, highest, compareAt: firstVariant(product)?.compareAtPrice ?? null };
};

export type Margin = { readonly amount: Money; readonly share: number | null };

export const marginOf = (price: Money, cost: Money): Margin => {
  const amount = subtractAmounts(price.amount, cost.amount);
  return {
    amount: { amount, currency: price.currency },
    share: isAmountAbove(price.amount, '0') ? amountShare(amount, price.amount) : null,
  };
};

export const formatMargin = (margin: Margin, locale: string): string => {
  const amount = formatMoney(margin.amount, locale);
  if (margin.share === null) return amount;
  const share = new Intl.NumberFormat(locale, { style: 'percent', maximumFractionDigits: 0 }).format(
    margin.share,
  );
  return `${amount} · ${share}`;
};
